import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';
import { CategoriaClienteRepository } from 'src/app/domain/service/categoria-cliente/categoria-cliente-repository';
import { HttpCatalogoSeleccionService } from 'src/app/infrastructure/adapters/catalogo-seleccion/http-catalogo-seleccion.service';
import {
  BusquedaAseguradoraDialogComponent
} from 'src/app/presentation/shared/buscadores/buscador-aseguradora-dialog.component';
import { AseguradoraSeleccionada } from 'src/app/presentation/shared/buscadores/buscador-aseguradora-dialog.component';
import { AsociacionCatalogoComponent } from './asociacion-catalogo.component';
import {
  configCategoriaCliente,
  configEspecialidad,
  configServicio,
  configUnidadNegocio,
  configZona
} from './asociacion-catalogo.configs';

/**
 * Diálogo "Asociaciones" de un Empleado (HU1259149, Fase 3) — 5 tabs, uno
 * por catálogo (Servicios, Especialidades, Unidades de Negocio, Categorías
 * Cliente, Zonas), cada uno un {@link AsociacionCatalogoComponent}
 * independiente. Abierto desde `ModificarEmpleadoComponent` (Fase 3 §1.6).
 *
 * <p>Unidades de Negocio y Categorías Cliente requieren elegir un cliente
 * primero (asociación por cliente, Fase 0/1) — reutiliza
 * {@code BusquedaAseguradoraDialogComponent} (TPSC_CLIENTES = "aseguradora"
 * en el vocabulario de este proyecto, ver {@code AseguradoraRepository}).</p>
 */
@Component({
  selector: 'app-asociaciones-empleado-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, TabsModule, ButtonModule, AsociacionCatalogoComponent, BusquedaAseguradoraDialogComponent],
  template: `
    <p-dialog
      header="Asociaciones del Empleado"
      [(visible)]="visibleInterno"
      [modal]="true"
      [style]="{ width: '95vw', maxWidth: '60rem' }"
      [contentStyle]="{ 'max-height': '85vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '92vw', '768px': '95vw', '640px': '98vw' }"
      [closable]="true"
      [draggable]="false"
      (onHide)="cerrar()"
    >
      <p-tabs [value]="tabActivo()" (valueChange)="tabActivo.set(+$event)">
        <p-tablist>
          <p-tab [value]="0"><i class="pi pi-briefcase mr-2"></i>Servicios</p-tab>
          <p-tab [value]="1"><i class="pi pi-verified mr-2"></i>Especialidades</p-tab>
          <p-tab [value]="2"><i class="pi pi-building mr-2"></i>Unidades de Negocio</p-tab>
          <p-tab [value]="3"><i class="pi pi-tags mr-2"></i>Categorías Cliente</p-tab>
          <p-tab [value]="4"><i class="pi pi-map mr-2"></i>Zonas</p-tab>
        </p-tablist>

        <p-tabpanels>
          <p-tabpanel [value]="0">
            <app-asociacion-catalogo [config]="configs.servicio" [nmconsecutivoEmpleado]="nmconsecutivoEmpleado"></app-asociacion-catalogo>
          </p-tabpanel>
          <p-tabpanel [value]="1">
            <app-asociacion-catalogo [config]="configs.especialidad" [nmconsecutivoEmpleado]="nmconsecutivoEmpleado"></app-asociacion-catalogo>
          </p-tabpanel>
          <p-tabpanel [value]="2">
            @if (!clienteSeleccionado()) {
              <div class="p-3 mb-3 rounded bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm flex items-center justify-between gap-2">
                <span>Seleccione un cliente para ver/agregar sus Unidades de Negocio.</span>
                <p-button label="Seleccionar cliente" icon="pi pi-search" styleClass="min-h-[44px]" (onClick)="mostrarBuscadorCliente.set(true)"></p-button>
              </div>
            } @else {
              <div class="p-2 mb-3 text-sm text-gray-600 dark:text-gray-300 flex items-center justify-between">
                <span>Cliente: <strong>{{ clienteSeleccionado()?.nombreComercial }}</strong></span>
                <p-button label="Cambiar" [text]="true" styleClass="min-h-[44px]" (onClick)="mostrarBuscadorCliente.set(true)"></p-button>
              </div>
              <app-asociacion-catalogo
                [config]="configs.unidadNegocio"
                [nmconsecutivoEmpleado]="nmconsecutivoEmpleado"
                [nmconsecutivoCliente]="clienteSeleccionado()?.nmconsecutivo ?? null"
                [nombreComercialCliente]="clienteSeleccionado()?.nombreComercial ?? null"
              ></app-asociacion-catalogo>
            }
          </p-tabpanel>
          <p-tabpanel [value]="3">
            @if (!clienteSeleccionado()) {
              <div class="p-3 mb-3 rounded bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm flex items-center justify-between gap-2">
                <span>Seleccione un cliente para ver/agregar sus Categorías.</span>
                <p-button label="Seleccionar cliente" icon="pi pi-search" styleClass="min-h-[44px]" (onClick)="mostrarBuscadorCliente.set(true)"></p-button>
              </div>
            } @else {
              <div class="p-2 mb-3 text-sm text-gray-600 dark:text-gray-300 flex items-center justify-between">
                <span>Cliente: <strong>{{ clienteSeleccionado()?.nombreComercial }}</strong></span>
                <p-button label="Cambiar" [text]="true" styleClass="min-h-[44px]" (onClick)="mostrarBuscadorCliente.set(true)"></p-button>
              </div>
              <app-asociacion-catalogo
                [config]="configs.categoriaCliente"
                [nmconsecutivoEmpleado]="nmconsecutivoEmpleado"
                [nmconsecutivoCliente]="clienteSeleccionado()?.nmconsecutivo ?? null"
                [nombreComercialCliente]="clienteSeleccionado()?.nombreComercial ?? null"
              ></app-asociacion-catalogo>
            }
          </p-tabpanel>
          <p-tabpanel [value]="4">
            <app-asociacion-catalogo [config]="configs.zona" [nmconsecutivoEmpleado]="nmconsecutivoEmpleado"></app-asociacion-catalogo>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    </p-dialog>

    @if (mostrarBuscadorCliente()) {
      <!--
        Defaults del legado (ConsultarClientesViewImpl): el listado arranca en
        Cédula de Ciudadanía y con Activo = "Todos" (rbgActivo = ALL_VALUE).
      -->
      <app-busqueda-aseguradora-dialog
        tipoIdentificacionDefault="C"
        activoDefault=""
        (aseguradoraSelected)="onClienteSeleccionado($event)"
        (dialogClosed)="mostrarBuscadorCliente.set(false)"
      ></app-busqueda-aseguradora-dialog>
    }
  `
})
export class AsociacionesEmpleadoDialogComponent {

  @Input({ required: true }) visible = false;
  @Input({ required: true }) nmconsecutivoEmpleado!: number;
  @Output() visibleChange = new EventEmitter<boolean>();

  protected readonly tabActivo = signal(0);
  protected readonly mostrarBuscadorCliente = signal(false);
  protected readonly clienteSeleccionado = signal<AseguradoraSeleccionada | null>(null);

  private readonly categoriaClienteRepository = inject(CategoriaClienteRepository);
  private readonly catalogoSeleccionService = inject(HttpCatalogoSeleccionService);

  protected readonly configs = {
    servicio: configServicio(this.catalogoSeleccionService),
    especialidad: configEspecialidad(this.catalogoSeleccionService),
    unidadNegocio: configUnidadNegocio(this.catalogoSeleccionService),
    categoriaCliente: configCategoriaCliente(this.categoriaClienteRepository),
    zona: configZona(this.catalogoSeleccionService)
  };

  get visibleInterno(): boolean {
    return this.visible;
  }

  set visibleInterno(value: boolean) {
    this.visibleChange.emit(value);
  }

  onClienteSeleccionado(cliente: AseguradoraSeleccionada): void {
    this.clienteSeleccionado.set(cliente);
    this.mostrarBuscadorCliente.set(false);
  }

  cerrar(): void {
    this.visibleChange.emit(false);
  }
}
